import React, { useMemo } from "react";
import { Inbox, Loader2, RefreshCw, Zap } from "lucide-react";
import {
  formatCoinTotal,
  formatPrice01,
  getOrderBookSummary,
  getSelectedOutcomeLabel,
} from "../../../utils/bondPredictDetailHelpers";

const DepthColumn = ({ title, levels, tone, emptyLabel }) => (
  <div className={`bp-detail-depth-col bp-detail-depth-${tone}`}>
    <div className="bp-detail-depth-col-head">
      <span>{title}</span>
      <em>{levels.length}</em>
    </div>
    {levels.length ? (
      <div className="bp-detail-depth-rows">
        <div className="bp-detail-depth-row bp-detail-depth-row-head">
          <span>Price</span>
          <span>Size</span>
          <span>Total</span>
        </div>
        {levels.map((lvl) => (
          <div key={`${tone}-${lvl.price}`} className="bp-detail-depth-row">
            <span>{formatPrice01(lvl.price)}</span>
            <span>{lvl.quantity.toFixed(1)}</span>
            <span>{formatCoinTotal(lvl.price, lvl.quantity)}</span>
          </div>
        ))}
      </div>
    ) : (
      <div className="bp-detail-depth-empty">
        <Inbox size={14} />
        <span>{emptyLabel}</span>
      </div>
    )}
  </div>
);

const MarketDetailOrderBook = ({
  market,
  selectedOutcome,
  orderBook,
  orderBookStatus,
  onRefresh,
  onTrade,
  tradeDisabled,
  tradeLabel,
}) => {
  const summary = useMemo(() => getOrderBookSummary(orderBook), [orderBook]);
  const lastTraded =
    typeof selectedOutcome?.metrics?.lastTradePrice === "number"
      ? selectedOutcome.metrics.lastTradePrice
      : null;
  const label = getSelectedOutcomeLabel(selectedOutcome, market);
  const loading = orderBookStatus === "loading" && !summary.bids.length && !summary.asks.length;
  const tradeLabelText = tradeLabel || "Trade";

  return (
    <section className="bp-detail-panel bp-detail-panel-depth">
      <div className="bp-detail-panel-head">
        <div>
          <p className="bp-detail-panel-kicker">Market depth</p>
          <h2>{selectedOutcome ? label : "Select an outcome"}</h2>
        </div>
        <div className="bp-detail-panel-head-actions">
          <button
            type="button"
            className="bp-icon-btn"
            onClick={onRefresh}
            disabled={!selectedOutcome || orderBookStatus === "loading"}
            aria-label="Refresh order book"
          >
            <RefreshCw size={14} className={orderBookStatus === "loading" ? "bp-spin" : ""} />
          </button>
          <button
            type="button"
            className="bp-detail-depth-trade-btn"
            onClick={onTrade}
            disabled={tradeDisabled}
            aria-label={tradeLabelText}
            title={tradeLabelText}
          >
            <Zap size={13} strokeWidth={2.4} />
            <span>{tradeLabelText}</span>
          </button>
        </div>
      </div>

      <div className="bp-detail-price-bar">
        <div>
          <span>Best bid</span>
          <strong className="bp-detail-price-bid">
            {summary.bestBid != null ? formatPrice01(summary.bestBid) : "—"}
          </strong>
        </div>
        <div>
          <span>Spread</span>
          <strong>{summary.spread != null ? formatPrice01(summary.spread) : "—"}</strong>
        </div>
        <div>
          <span>Best ask</span>
          <strong className="bp-detail-price-ask">
            {summary.bestAsk != null ? formatPrice01(summary.bestAsk) : "—"}
          </strong>
        </div>
        <div>
          <span>Last</span>
          <strong>{lastTraded != null ? formatPrice01(lastTraded) : "—"}</strong>
        </div>
      </div>

      {loading ? (
        <div className="bp-detail-book-loading">
          <Loader2 size={20} className="bp-spin" />
        </div>
      ) : (
        <div className="bp-detail-depth-grid">
          <DepthColumn
            title="Bids"
            levels={summary.bids}
            tone="bid"
            emptyLabel="No bids"
          />
          <DepthColumn
            title="Asks"
            levels={summary.asks}
            tone="ask"
            emptyLabel="No asks"
          />
        </div>
      )}
    </section>
  );
};

export default MarketDetailOrderBook;
